/**
 * IntegrationenAdmin — /admin/integrationen (T-C2c, #1643 OAuth-Umbau)
 *
 * Admin-only Verwaltung der OAuth-autorisierten Verbindungen (Claude.ai-Connectoren
 * u.ä.). Es werden KEINE Roh-Tokens mehr generiert — die Anbindung läuft über den
 * OAuth-2.1-Flow (Login + Consent in der App). Diese Seite listet die erteilten
 * Grants (Connector, Scopes, User, autorisiert am, zuletzt genutzt, Status) und
 * erlaubt das sofortige Widerrufen. Enthält die Connector-Karte Warehouse-MCP inkl.
 * Claude.ai-Anleitung.
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useDialog } from '../../hooks/useDialog';
import { Plug, RefreshCw, Ban, Link2 } from 'lucide-react';

const API = '';
const MCP_URL = `${window.location.origin}/mcp/warehouse`;

export default function IntegrationenAdmin() {
  const { t, language } = useLanguage();
  const dateLocale = (language || 'de') === 'de' ? 'de-AT' : 'en-GB';
  const dlg = useDialog();

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/oauth/connections`, { credentials: 'include' });
      const d = await r.json();
      setConnections(d.connections || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function fmtDate(v) {
    return v ? new Date(v).toLocaleString(dateLocale, { dateStyle: 'short', timeStyle: 'short' }) : '—';
  }

  async function doRevoke(conn) {
    const ok = await dlg.confirm(
      t('integrationen.revokeTitle'),
      t('integrationen.revokeConfirm', { name: conn.client_name }),
      { confirmLabel: t('integrationen.revoke'), variant: 'danger' }
    );
    if (!ok) return;
    const r = await fetch(`${API}/api/oauth/connections/${conn.id}/revoke`, { method: 'POST', credentials: 'include' });
    if (!r.ok) { dlg.alert(t('common.error'), t('integrationen.actionFailed')); return; }
    load();
  }

  return (
    <div className="p-6 w-full max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--primary,#338edc)]/10 flex items-center justify-center">
            <Plug size={20} className="text-[var(--primary,#338edc)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('integrationen.title')}</h1>
            <p className="text-sm text-[var(--text-tertiary)]">{t('integrationen.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--border-card,#E5E7EB)] text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
        >
          <RefreshCw size={14} /> {t('common.refresh')}
        </button>
      </div>

      {/* Connector-Karte Warehouse-MCP */}
      <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Link2 size={16} className="text-[var(--text-secondary)]" />
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('integrationen.connectorTitle')}</h2>
          <span className="px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[10px] font-mono text-[var(--text-tertiary)]">warehouse-placement</span>
        </div>
        <p className="text-xs text-[var(--text-tertiary)] mb-3 leading-relaxed">{t('integrationen.connectorDesc')}</p>
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">{t('integrationen.endpointUrl')}</div>
          <code className="block px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-xs font-mono text-[var(--text-primary)] break-all">{MCP_URL}</code>
        </div>
        <ol className="text-xs text-[var(--text-secondary)] space-y-1.5 list-decimal list-inside leading-relaxed">
          <li>{t('integrationen.step1')}</li>
          <li>{t('integrationen.step2')}</li>
          <li>{t('integrationen.step3')}</li>
          <li>{t('integrationen.step4')}</li>
        </ol>
      </div>

      {/* Autorisierte Verbindungen */}
      <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('integrationen.connectionsTitle')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)] text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                <th className="px-3 py-2 text-left">{t('integrationen.colConnector')}</th>
                <th className="px-3 py-2 text-left">{t('integrationen.colScope')}</th>
                <th className="px-3 py-2 text-left">{t('integrationen.colUser')}</th>
                <th className="px-3 py-2 text-left">{t('integrationen.colAuthorized')}</th>
                <th className="px-3 py-2 text-left">{t('integrationen.colLastUsed')}</th>
                <th className="px-3 py-2 text-left">{t('integrationen.colStatus')}</th>
                <th className="px-3 py-2 text-right">{t('integrationen.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-card,#E5E7EB)]/50">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('common.loading')}…</td></tr>
              )}
              {!loading && connections.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('integrationen.noConnections')}</td></tr>
              )}
              {connections.map(conn => (
                <tr key={conn.id} className="hover:bg-[var(--bg-inset,#F8F9FC)]">
                  <td className="px-3 py-2 font-medium text-[var(--text-primary)]">{conn.client_name}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {conn.scopes.map(s => (
                        <span key={s} className="px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[10px] font-mono text-[var(--text-secondary)]">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{conn.user_name || `#${conn.user_id}`}</td>
                  <td className="px-3 py-2 text-[var(--text-tertiary)] whitespace-nowrap">{fmtDate(conn.granted_at)}</td>
                  <td className="px-3 py-2 text-[var(--text-tertiary)] whitespace-nowrap">{fmtDate(conn.last_used_at)}</td>
                  <td className="px-3 py-2">
                    {conn.status === 'active'
                      ? <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 text-[10px] font-semibold">{t('integrationen.statusActive')}</span>
                      : <span className="px-1.5 py-0.5 rounded bg-slate-500/10 text-slate-600 text-[10px] font-semibold">{t('integrationen.statusRevoked')}</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {conn.status === 'active' && (
                      <button onClick={() => doRevoke(conn)} title={t('integrationen.revoke')} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                        <Ban size={12} /> {t('integrationen.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {dlg.element}
    </div>
  );
}
